import type React from 'react';

import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';

import { CaseSources } from './jmrUtils';

const ALLOWED_EMAIL_DOMAINS = ['amazon.com', 'sedgwick.com', 'sedgwickcms.com', 'origamirisk.com'];
const EMAIL_REGEX = /^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,7}$/;

// **** Functions **** //

export const pluralizeString = (str: string, quantity: number) => `${str}${quantity > 1 ? 's' : ''}`;

export const arrayMove = (arr: any[], fromIndex: number, toIndex: number) => {
  const element = arr[fromIndex];
  arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, element);
  return arr;
};

export const getAustinLink = (caseId: string) => {
  return window.location.host === 'prod.na.rtw.whs.amazon.dev'
    ? `https://na.ehs-amazon.com/incidents/incident/${caseId}/summary`
    : `https://na-gamma.austin.a2z.com/incidents/incident/${caseId}/summary`;
};

export const isEmailValid = (value: string) => {
  if (!value) {
    return true;
  }

  // remove spaces, then split by semicolon
  const emails = value.replace(/\s+/g, '').split(';');
  for (const email of emails) {
    let trimmedEmail = email.trim();
    if (!trimmedEmail.includes('@')) {
      trimmedEmail = trimmedEmail + '@amazon.com';
    }
    if (!email.match(EMAIL_REGEX) || !ALLOWED_EMAIL_DOMAINS.includes(email.split('@')[1].toLowerCase())) {
      return false;
    }
  }
  return true;
};

export const linkClickHandler = (url: string) => {
  window.open(url, '_blank');
};

export const removeSpecialCharactersFromString = (str: string) => {
  return str.replace(/[^a-zA-Z0-9_]/g, '');
};

export const isNumber = (value?: string | number): value is number => {
  return value != null && value !== '' && !isNaN(Number(value.toString()));
};

export const getExampleCaseIdText = (caseSource: string) => {
  if (caseSource === CaseSources.Austin) {
    return 'e.g. ABE4-23-112';
  } else {
    return 'e.g. A-2023-825678';
  }
};

export const stripOutLeadingHash = (caseId: string) => {
  return caseId && caseId.replace(/^#/, '');
};

/*
  Check if a value is within a numeric range.
  e.g. for lowerBound = 0 and upperBound = 40
    4 = true
    "3.5" = true
    100 = false
    "-2" = false
    "a" = false
    "" = false
    " " = false
    undefined = false
    null = false
 */
export const isWithinRange = (value: any, lowerBound: number, upperBound: number): boolean => {
  // after stringify, possible values are "3.5", "100", "-2", "a", "", "undefined", "null"
  const stringValue: string = String(value).trim();
  return (
    stringValue !== '' && // detect ""
    +stringValue >= lowerBound && // detect "-2", "a", "undefined", "null"
    +stringValue <= upperBound
  ); // detect "100", which is larger than shift length
};

/**
 * Compare two primitive values. If a param is a string, trim and lowercase it.
 */
export const arePrimitiveValuesDiff = (a: string | number | boolean, b: string | number | boolean): boolean => {
  if (typeof a === 'string') {
    a = a.trim().toLowerCase();
  }
  if (typeof b === 'string') {
    b = b.trim().toLowerCase();
  }
  return a !== b;
};

/**
 * Sometimes we want to allow "0" and "false" when checking truthy.
 */
export const isNotEmpty = <T>(val: T): val is NonNullable<T> => val !== '' && val !== null && val !== undefined;

/**
 * Sometimes we want to allow "0" and "false" when checking truthy.
 */
export const isEmpty = <T>(val: T): val is NonNullable<T> => !isNotEmpty(val);

// Canada Type guard
export const isCanadianSite = (countryId: number): boolean => {
  return countryId === COUNTRY_ID_MAPPING.CANADA_COUNTRY_ID;
};

/** Note that objects are compared by reference for the purposes of Set equality so this should only be used with
 * primitive types */
export const deduplicateArray = <T extends number | string | undefined>(inputArray: T[]) => {
  return [...new Set<T>(inputArray)];
};

/**
 * A lot times me might want to append an error message to a string but we don't know the format of it. So we
 * don't have to do the check every time use this function. If you don't wanna use the prepend and just get the
 * error message, just pass an empty string as the prepend.
 */
export const appendError = (prepend: string, error: unknown) => {
  if (error instanceof Error) {
    return `${prepend}: ${error.message}`;
  } else if (typeof error === 'string') {
    return `${prepend}: ${error}`;
  } else {
    return prepend;
  }
};

/**
 * A more type safe version of Object.entries.
 */
export const getObjectEntries = <T extends object, U extends keyof T>(param: T): [U, T[U]][] => {
  return Object.entries(param).map((entry) => [entry[0] as U, entry[1] as T[U]]);
};

/**
 * Sanitize a given string so that it is valid for use as part of an HTML attribute value.
 */
export const htmlAttributeSanitize = (value: string): string => {
  return value.toLowerCase().replace(' ', '_').replace("'", '');
};

/** Takes in a list of strings and joins the last two elements with 'and' while all preceding elements are joined with
 * commas */
export const grammarJoin = (values: string[] | React.JSX.Element[]): (string | React.JSX.Element)[] => {
  if (values.length == 0) {
    return [''];
  }
  if (values.length == 1) {
    return [values[0]];
  }
  // don't include Oxford comma if there are only two items
  if (values.length == 2) {
    return [values[0], ' and ', values[1]];
  }
  const result: (string | React.JSX.Element)[] = [];
  values.forEach((v) => result.push(...[v, ', ']));
  result.push(...['and ', values[values.length - 1]]);
  return result;
};
